import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, Minus, Plus } from 'lucide-react';
import { Button, cn } from '@mlm/ui';
import { EmptyState } from '../components/empty-state';
import { createOrder, getRedeemCatalog, productImageUrl, type StorefrontProduct } from '../lib/api';
import { fmtNumber } from '../lib/format';
import { useMySlots } from '../lib/hooks/use-my-slots';

/**
 * GC Redeem store. Redeem-only products priced in GC (flat `priceGc`, no PV).
 * Self-contained — its own quantity state, never mixed with the normal cart —
 * and checks out as a single GC order (`currency: 'gc'`). The backend rejects
 * any cart that mixes redeem + normal items as a backstop.
 */
export function RedeemPage() {
  const qc = useQueryClient();
  // The GC balance shown (and spent) is the SELECTED slot's, so the catalog must
  // be fetched and cached against that same slot.
  const { activeSlotId } = useMySlots();
  const query = useQuery({
    queryKey: ['redeem-catalog', activeSlotId],
    queryFn: () => getRedeemCatalog(activeSlotId),
  });
  const [qty, setQty] = useState<Record<string, number>>({});
  const [placed, setPlaced] = useState(false);

  // A redeem item is bought with GC and nothing else, so one without a GC price
  // (never priced in GC, or its "sold in GC" switch is off) simply isn't
  // redeemable. The API already drops those; skipping them here too means a
  // stale cache can't render an unbuyable "0 GC" card the member can tap.
  const products = useMemo(
    () => (query.data?.products ?? []).filter((p) => p.priceGc != null),
    [query.data],
  );
  const gcBalance = Number(query.data?.context.balances.gc ?? '0');

  const items = useMemo(
    () => products.map((p) => ({ product: p, qty: qty[p.id] ?? 0 })).filter((r) => r.qty > 0),
    [products, qty],
  );
  const total = items.reduce((sum, r) => sum + Number(r.product.priceGc ?? 0) * r.qty, 0);
  const insufficient = total > gcBalance;

  const place = useMutation({
    meta: { successMessage: 'Order placed.' },
    mutationFn: () =>
      createOrder({
        type: 'ecom',
        currency: 'gc',
        items: items.map((r) => ({ productId: r.product.id, qty: r.qty })),
      }),
    onSuccess: () => {
      setQty({});
      setPlaced(true);
      qc.invalidateQueries({ queryKey: ['redeem-catalog'] });
      qc.invalidateQueries({ queryKey: ['wallet'] });
    },
  });

  function bump(id: string, delta: number) {
    setQty((prev) => {
      const next = Math.max(0, (prev[id] ?? 0) + delta);
      return { ...prev, [id]: next };
    });
  }

  const errMsg = (
    place.error as { response?: { data?: { message?: string; error?: string } } } | null
  )?.response?.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-h1">
            <Gift className="h-6 w-6 text-muted-foreground" /> Redeem
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Redeem these items using your GC balance.
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-muted-foreground">GC balance</p>
          <p className="text-lg font-semibold">{fmtNumber(gcBalance)} GC</p>
        </div>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : products.length === 0 ? (
        <EmptyState
          icon={Gift}
          title="No redeemable items"
          description="There are no items available to redeem right now. Check back later."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <RedeemCard key={p.id} product={p} qty={qty[p.id] ?? 0} onBump={(d) => bump(p.id, d)} />
          ))}
        </div>
      )}

      {items.length > 0 && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card p-4 shadow-lg">
          <div>
            <p className="text-sm text-muted-foreground">
              {items.reduce((a, r) => a + r.qty, 0)} item(s) selected
            </p>
            <p className="text-lg font-semibold">{fmtNumber(total)} GC</p>
            {insufficient && (
              <p className="text-sm text-destructive">Not enough GC for this redemption.</p>
            )}
            {errMsg && <p className="text-sm text-destructive">{errMsg.message ?? errMsg.error}</p>}
          </div>
          <Button disabled={insufficient || place.isPending} onClick={() => place.mutate()}>
            {place.isPending ? 'Redeeming…' : 'Redeem with GC'}
          </Button>
        </div>
      )}

      {placed && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPlaced(false)}
        >
          <div
            className="w-full max-w-sm rounded-lg bg-card p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <Gift className="mx-auto h-10 w-10 text-primary" />
            <h2 className="mt-3 text-lg font-semibold">Redeemed!</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Your redemption is complete. Track it in your Orders.
            </p>
            <Button className="mt-4 w-full" onClick={() => setPlaced(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RedeemCard({
  product,
  qty,
  onBump,
}: {
  product: StorefrontProduct;
  qty: number;
  onBump: (delta: number) => void;
}) {
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="aspect-square w-full bg-muted">
        {product.images.length > 0 ? (
          <img
            src={`${productImageUrl(product.id, 0)}?v=${encodeURIComponent(product.images[0]!)}`}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Gift className="h-8 w-8 text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="space-y-2 p-3">
        <p className="font-medium leading-tight">{product.name}</p>
        <p className="text-sm font-semibold text-primary">
          {fmtNumber(Number(product.priceGc ?? 0))} GC
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={qty <= 0}
              onClick={() => onBump(-1)}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className={cn('w-6 text-center text-sm', qty > 0 && 'font-semibold')}>{qty}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => onBump(1)}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
